import React, { useEffect, useMemo, useState } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon, ChartPieIcon, ClockIcon, BeakerIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds } from '../../hooks/useApi';
import apiClient from '../../utils/api';

const InventorySnapshot: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [speciesInsights, setSpeciesInsights] = useState<any>(null);
  const [densityAnalysis, setDensityAnalysis] = useState<any[]>([]);
  const [isMethodologyExpanded, setIsMethodologyExpanded] = useState(false);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);

  const analyzeSpeciesInventory = (pondsData: any[], filterPondId?: string) => {
    // Filter ponds if a specific pond is selected
    const filteredPonds = filterPondId 
      ? pondsData.filter(pond => pond.id === filterPondId)
      : pondsData;
      
    // Helper function to normalize species names to match master data casing
    const normalizeSpeciesName = (species: string): string => {
      const normalized = species.toLowerCase().trim();
      const masterDataMapping: Record<string, string> = {
        'rohu': 'Rohu',
        'catla': 'Catla', 
        'mrigal': 'Mrigal',
        'grass carp': 'Grass Carp',
        'silver carp': 'Silver Carp',
        'bighead carp': 'Bighead Carp',
        'tilapia': 'Tilapia',
        'pangash': 'Pangash',
        'pangasius': 'Pangasius',
        'common carp': 'Common Carp'
      };
      return masterDataMapping[normalized] || species; // Return original if no mapping found
    };
      
    const insights = {
      totalSpecies: 0,
      speciesDistribution: {} as Record<string, { biomass: number; count: number; ponds: number }>,
      mixCulturePonds: 0,
      singleSpeciesPonds: 0,
      emptyPonds: 0,
      avgWeightBySpecies: {} as Record<string, number>,
      lastUpdatedBySpecies: {} as Record<string, string>,
      totalBiomass: 0,
      totalFishCount: 0
    };

    filteredPonds.forEach(pond => {
      const speciesInventory = pond.species_inventory || {};
      const speciesCount = Object.keys(speciesInventory).length;
      
      if (speciesCount === 0) {
        insights.emptyPonds++;
      } else if (speciesCount === 1) {
        insights.singleSpeciesPonds++;
      } else {
        insights.mixCulturePonds++;
      }

      Object.entries(speciesInventory).forEach(([species, data]: [string, any]) => {
        const normalizedSpecies = normalizeSpeciesName(species);
        
        if (!insights.speciesDistribution[normalizedSpecies]) {
          insights.speciesDistribution[normalizedSpecies] = { biomass: 0, count: 0, ponds: 0 };
        }
        
        insights.speciesDistribution[normalizedSpecies].biomass += data.biomass_kg || 0;
        insights.speciesDistribution[normalizedSpecies].count += data.count || 0;
        insights.speciesDistribution[normalizedSpecies].ponds++;
        
        insights.totalBiomass += data.biomass_kg || 0;
        insights.totalFishCount += data.count || 0;
        
        if (data.avg_weight_g && data.avg_weight_g > 0) {
          if (!insights.avgWeightBySpecies[normalizedSpecies]) {
            insights.avgWeightBySpecies[normalizedSpecies] = 0;
          }
          // For now, just use the latest average weight
          // In a more sophisticated version, we could track multiple weights per species
          insights.avgWeightBySpecies[normalizedSpecies] = data.avg_weight_g;
        }
        
        if (data.last_updated) {
          if (!insights.lastUpdatedBySpecies[normalizedSpecies] || data.last_updated > insights.lastUpdatedBySpecies[normalizedSpecies]) {
            insights.lastUpdatedBySpecies[normalizedSpecies] = data.last_updated;
          }
        }
      });
    });

    insights.totalSpecies = Object.keys(insights.speciesDistribution).length;

    return insights;
  };

  const fetchData = async () => {
    if (!selectedLocation) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/reports/inventory-snapshot', {
        params: {
          location_id: selectedLocation.id,
          pond_id: selectedPondId || undefined,
        },
      });
      setRows(res.data || []);
      
      // Fetch pond data for species insights
      const pondsRes = await apiClient.get(`/locations/${selectedLocation.id}/ponds`);
      const insights = analyzeSpeciesInventory(pondsRes.data || [], selectedPondId || undefined);
      setSpeciesInsights(insights);
      
      // Fetch density analysis
      const densityResponse = await apiClient.get('/reports/density-analysis', {
        params: {
          location_id: selectedLocation.id,
          pond_id: selectedPondId || undefined,
        },
      });
      setDensityAnalysis(densityResponse.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load snapshot');
      setRows([]);
      setSpeciesInsights(null);
      setDensityAnalysis([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, selectedPondId]);

  const downloadCsv = async () => {
    if (!selectedLocation) return;
    try {
      const res = await apiClient.get(`/reports/export/inventory-snapshot`, {
        params: {
          location_id: selectedLocation.id,
          pond_id: selectedPondId || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_snapshot.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="flex items-center gap-2">
        <ChartBarIcon className="h-6 w-6 text-indigo-600" />
        <h1 className="text-2xl font-bold text-gray-900">Inventory & Growth Snapshot</h1>
      </div>

      {/* Pond Filter Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ChartBarIcon className="h-5 w-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filter & Controls</h2>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Pond</label>
            <select 
              value={selectedPondId} 
              onChange={(e) => setSelectedPondId(e.target.value)} 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Ponds ({pondOptions.length})</option>
              {pondOptions.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {selectedPondId ? 'Showing data for selected pond only' : 'Showing aggregated data for all ponds'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchData} 
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
            >
              Refresh Data
            </button>
            <button 
              onClick={downloadCsv} 
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" /> CSV
            </button>
          </div>
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 text-indigo-800 text-sm rounded-lg p-4">
        <button
          onClick={() => setIsMethodologyExpanded(!isMethodologyExpanded)}
          className="flex items-center justify-between w-full text-left hover:bg-indigo-100 rounded-lg p-2 -m-2 transition-colors"
        >
          <div className="font-medium">📊 Data Sources & Methodology</div>
          {isMethodologyExpanded ? (
            <ChevronDownIcon className="h-4 w-4 text-indigo-600" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-indigo-600" />
          )}
        </button>
        
        {isMethodologyExpanded && (
          <div className="mt-3 space-y-3">
          
          <div>
            <div className="font-medium text-indigo-900 mb-1">🐟 Biomass (kg) - Primary Data Source</div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li><span className="font-medium">species_inventory:</span> Sum of all species biomass from pond's species inventory (preferred method)</li>
              <li><span className="font-medium">sampling:</span> Latest fish sampling estimate if available and more recent than species inventory</li>
              <li><span className="font-medium">cycle_count:</span> Manual reconciliation data from Cycle Count reports</li>
              <li><span className="font-medium">transfer:</span> Recent fish transfers (last 30 days) affecting biomass</li>
              <li><span className="font-medium">counter:</span> Legacy pond counter values (fallback only)</li>
            </ul>
          </div>

          <div>
            <div className="font-medium text-indigo-900 mb-1">🔢 Fish Count - Current Inventory</div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li>Sum of fish counts from species inventory (primary)</li>
              <li>Updated by: Fish Seed logs, Dead Fish logs, Transfers, Cycle Count</li>
              <li>Fallback to legacy pond counter if species inventory empty</li>
            </ul>
          </div>

          <div>
            <div className="font-medium text-indigo-900 mb-1">⚖️ Average Fish Weight (g) - Calculated</div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li>Formula: <code className="bg-indigo-100 px-1 rounded">(Biomass × 1000) ÷ Fish Count</code></li>
              <li>Shows "—" when fish count is 0 or biomass is 0</li>
              <li>Automatically calculated from current biomass and fish count</li>
            </ul>
          </div>

          <div>
            <div className="font-medium text-indigo-900 mb-1">💰 Layer Qty (kg) - FIFO Cost Tracking</div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li>Remaining quantity from FIFO cost layers in <code className="bg-indigo-100 px-1 rounded">pond_fish_layers</code></li>
              <li>Updated by: Fish Seed logs, Dead Fish logs, Transfers, Cycle Count</li>
              <li>Used to calculate Cost per kg and Total Cost</li>
              <li>Automatically adjusted when cycle counts are performed</li>
            </ul>
          </div>

          <div>
            <div className="font-medium text-indigo-900 mb-1">💵 Cost / kg & Total Cost - Financial Tracking</div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li><span className="font-medium">Cost / kg:</span> Average cost from remaining FIFO layers</li>
              <li><span className="font-medium">Total Cost:</span> <code className="bg-indigo-100 px-1 rounded">Cost/kg × Current Biomass</code></li>
              <li>Shows "—" when no cost layers exist</li>
              <li>Helps track inventory valuation and COGS</li>
            </ul>
          </div>

          <div className="bg-indigo-100 border border-indigo-200 rounded p-2 mt-3">
            <div className="font-medium text-indigo-900 text-xs mb-1">🔄 Data Update Triggers</div>
            <div className="text-xs text-indigo-700">
              <span className="font-medium">Automatic:</span> Fish Seed, Dead Fish, Fish Sampling, Transfers, Cycle Count<br/>
              <span className="font-medium">Manual:</span> Cycle Count (Manual Reconciliation), Transfer Management
            </div>
          </div>

        </div>
        )}
      </div>

      {/* Profit/Loss Analysis Section */}
      {rows.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartPieIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Profit/Loss Analysis</h2>
            </div>
            <div className="text-sm text-gray-500">
              {selectedPondId ? (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  Filtered: {pondOptions.find((p: any) => p.id === selectedPondId)?.name || 'Selected Pond'}
                </span>
              ) : (
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                  All Ponds ({rows.length})
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Investment */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Investment</div>
              <div className="text-2xl font-bold text-gray-900">
                ₹{rows.reduce((sum, r) => sum + (r.total_cost || 0), 0).toLocaleString()}
              </div>
            </div>

            {/* Total Current Value */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 mb-1">Total Current Value</div>
              <div className="text-2xl font-bold text-green-700">
                ₹{rows.reduce((sum, r) => sum + (r.total_current_value || 0), 0).toLocaleString()}
              </div>
            </div>

            {/* Net Profit/Loss */}
            <div className={`rounded-lg p-4 ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className={`text-sm mb-1 ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                Net Profit/Loss
              </div>
              <div className={`text-2xl font-bold ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                ₹{rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0).toLocaleString()}
              </div>
            </div>

            {/* ROI Percentage */}
            <div className={`rounded-lg p-4 ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className={`text-sm mb-1 ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ROI
              </div>
              <div className={`text-2xl font-bold ${rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {(() => {
                  const totalCost = rows.reduce((sum, r) => sum + (r.total_cost || 0), 0);
                  const totalProfit = rows.reduce((sum, r) => sum + (r.profit_loss || 0), 0);
                  return totalCost > 0 ? `${((totalProfit / totalCost) * 100).toFixed(1)}%` : '—';
                })()}
              </div>
            </div>
          </div>

          {/* Top Performers */}
          <div className="mt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Top Performers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Most Profitable */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-green-600 mb-2">Most Profitable Pond</div>
                {(() => {
                  const profitablePonds = rows.filter(r => r.profit_loss > 0);
                  if (profitablePonds.length === 0) {
                    return <div className="text-gray-500 text-sm">No profitable ponds</div>;
                  }
                  const topPond = profitablePonds.reduce((max, pond) => pond.profit_loss > max.profit_loss ? pond : max);
                  return (
                    <div>
                      <div className="font-medium text-green-800">{topPond.pond_name}</div>
                      <div className="text-green-700">₹{topPond.profit_loss.toLocaleString()}</div>
                    </div>
                  );
                })()}
              </div>

              {/* Biggest Loss */}
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-sm text-red-600 mb-2">Biggest Loss Pond</div>
                {(() => {
                  const lossPonds = rows.filter(r => r.profit_loss < 0);
                  if (lossPonds.length === 0) {
                    return <div className="text-gray-500 text-sm">No loss-making ponds</div>;
                  }
                  const worstPond = lossPonds.reduce((min, pond) => pond.profit_loss < min.profit_loss ? pond : min);
                  return (
                    <div>
                      <div className="font-medium text-red-800">{worstPond.pond_name}</div>
                      <div className="text-red-700">₹{worstPond.profit_loss.toLocaleString()}</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Density Analysis Section */}
      {densityAnalysis.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Density Compliance Analysis</h2>
            </div>
            <div className="text-sm text-gray-500">
              {selectedPondId ? (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Filtered: {pondOptions.find((p: any) => p.id === selectedPondId)?.name || 'Selected Pond'}
                </span>
              ) : (
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                  All Ponds ({densityAnalysis.length})
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {densityAnalysis.map((pond) => (
              <div key={pond.pond_id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{pond.pond_name}</h3>
                    <p className="text-sm text-gray-500">
                      {pond.species_count} species • {pond.total_fish.toLocaleString()} total fish
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pond.status === 'green' ? 'bg-green-100 text-green-800' :
                      pond.status === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {pond.status_text}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {pond.compliance_score}% compliance
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(pond.species_details).map(([species, details]: [string, any]) => (
                    <div key={species} className="bg-gray-50 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 capitalize">
                          {species.replace('_', ' ')}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          details.deviation <= 10 ? 'bg-green-100 text-green-800' :
                          details.deviation <= 25 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {details.deviation}% dev
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Current:</span>
                          <span className="font-medium">{details.current_pct}%</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Ideal:</span>
                          <span className="font-medium">{details.ideal_pct}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Species Insights Section */}
      {speciesInsights && speciesInsights.totalSpecies > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BeakerIcon className="h-5 w-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Species Inventory Insights</h2>
            </div>
            <div className="text-sm text-gray-500">
              {selectedPondId ? (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Filtered: {pondOptions.find((p: any) => p.id === selectedPondId)?.name || 'Selected Pond'}
                </span>
              ) : (
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  All Ponds ({pondOptions.length})
                </span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Species */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{speciesInsights.totalSpecies}</div>
              <div className="text-sm text-blue-800">Species Types</div>
            </div>
            
            {/* Total Biomass */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{speciesInsights.totalBiomass.toFixed(1)} kg</div>
              <div className="text-sm text-green-800">Total Biomass</div>
            </div>
            
            {/* Total Fish Count */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">{speciesInsights.totalFishCount.toLocaleString()}</div>
              <div className="text-sm text-purple-800">Total Fish</div>
            </div>
            
            {/* Mix Culture Ponds */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="text-2xl font-bold text-orange-600">{speciesInsights.mixCulturePonds}</div>
              <div className="text-sm text-orange-800">Mix Culture Ponds</div>
            </div>
          </div>

          {/* Species Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ChartPieIcon className="h-4 w-4 text-indigo-600" />
                Species Distribution by Biomass
              </h3>
              <div className="space-y-2">
                {Object.entries(speciesInsights.speciesDistribution)
                  .sort(([,a], [,b]) => (b as any).biomass - (a as any).biomass)
                  .map(([species, data]: [string, any]) => {
                    const percentage = speciesInsights.totalBiomass > 0 
                      ? (data.biomass / speciesInsights.totalBiomass * 100).toFixed(1)
                      : '0';
                    return (
                      <div key={species} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                          <span className="font-medium text-gray-900">{species}</span>
                        </div>
                        <div className="text-right text-sm">
                          <div className="font-semibold">{data.biomass.toFixed(1)} kg</div>
                          <div className="text-gray-500">{percentage}%</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ClockIcon className="h-4 w-4 text-indigo-600" />
                Average Weight by Species
              </h3>
              <div className="space-y-2">
                {Object.entries(speciesInsights.avgWeightBySpecies)
                  .sort(([,a], [,b]) => (b as any) - (a as any))
                  .map(([species, avgWeight]: [string, any]) => (
                    <div key={species} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="font-medium text-gray-900">{species}</span>
                      <div className="text-right">
                        <span className="font-semibold text-indigo-600">{avgWeight.toFixed(0)}g</span>
                        <div className="text-xs text-gray-500">avg weight</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Pond Type Distribution */}
          <div className="mt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Pond Type Distribution</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-xl font-bold text-green-600">{speciesInsights.singleSpeciesPonds}</div>
                <div className="text-sm text-green-800">Single Species</div>
              </div>
              <div className="text-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="text-xl font-bold text-orange-600">{speciesInsights.mixCulturePonds}</div>
                <div className="text-sm text-orange-800">Mix Culture</div>
              </div>
              <div className="text-center p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="text-xl font-bold text-gray-600">{speciesInsights.emptyPonds}</div>
                <div className="text-sm text-gray-800">Empty</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Inventory Details</h2>
          <div className="text-sm text-gray-500">
            {selectedPondId ? (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Filtered: {pondOptions.find((p: any) => p.id === selectedPondId)?.name || 'Selected Pond'}
              </span>
            ) : (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                All Ponds ({pondOptions.length})
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
        )}

        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2">Pond</th>
                <th className="text-left px-3 py-2">Biomass (kg)</th>
                <th className="text-left px-3 py-2">Fish Count</th>
                <th className="text-left px-3 py-2">Avg Fish Weight (g)</th>
                <th className="text-left px-3 py-2">Biomass Source</th>
                <th className="text-left px-3 py-2">Layer Qty (kg)</th>
                <th className="text-left px-3 py-2">Cost / kg</th>
                <th className="text-left px-3 py-2">Total Cost</th>
                <th className="text-left px-3 py-2">Total Current Value</th>
                <th className="text-left px-3 py-2">Profit/Loss</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-3">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-3 text-gray-500">No data</td></tr>
              ) : (
                rows.map((r: any, idx: number) => (
                  <React.Fragment key={idx}>
                    <tr className="border-t">
                      <td className="px-3 py-2">{r.pond_name}</td>
                      <td className="px-3 py-2">{r.biomass_kg}</td>
                      <td className="px-3 py-2">{r.fish_count}</td>
                      <td className="px-3 py-2">{r.avg_fish_weight_g ?? '—'}</td>
                      <td className="px-3 py-2">{r.biomass_source}</td>
                      <td className="px-3 py-2">{r.layer_qty_kg}</td>
                      <td className="px-3 py-2">{r.cost_per_kg ?? '—'}</td>
                      <td className="px-3 py-2">{r.total_cost}</td>
                      <td className="px-3 py-2 font-medium text-green-600">₹{r.total_current_value?.toLocaleString() ?? '—'}</td>
                      <td className={`px-3 py-2 font-medium ${r.profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₹{r.profit_loss?.toLocaleString() ?? '—'}
                      </td>
                    </tr>
                    {r.species_breakdown && Object.keys(r.species_breakdown).length > 0 && (
                      <tr className="bg-gray-50">
                        <td colSpan={10} className="px-3 py-2">
                          <div className="text-xs text-gray-600">
                            <div className="font-medium mb-1">Species Breakdown:</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                              {Object.entries(r.species_breakdown).map(([species, data]: [string, any]) => (
                                <div key={species} className="bg-white rounded border p-2">
                                  <div className="font-medium text-gray-800 capitalize">{species.replace(/_/g, ' ')}</div>
                                  <div className="text-gray-600">
                                    <div>Count: {data.count.toLocaleString()}</div>
                                    <div>Weight: {data.biomass_kg} kg</div>
                                    <div>Avg: {data.avg_weight_g}g</div>
                                    <div className="text-red-600">Cost: ₹{data.cost?.toLocaleString() ?? '—'}</div>
                                    <div className="font-medium text-green-600">Value: ₹{data.current_value?.toLocaleString() ?? '—'}</div>
                                    {data.cost > 0 && data.current_value > 0 && (
                                      <div className={`text-xs font-medium ${data.current_value > data.cost ? 'text-green-600' : 'text-red-600'}`}>
                                        Profit: ₹{(data.current_value - data.cost).toLocaleString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventorySnapshot;


